/**
 * Projective mapping for the Bundle Builder box compositor.
 *
 * Ported verbatim from the marketing builder at traineracademy.org. A quad — measured from the box
 * render in 1024-image pixels — describes where the slot opening sits in the photograph; the
 * homography maps the flat 1024-unit stage onto it, so a rectangular book image lands in the box
 * with correct perspective.
 *
 * Pure maths, no dependencies. Keep it identical to the source: the geometry constants in
 * BundleBoxSet were calibrated against this exact solver.
 */

export interface Quad {
  /** left edge x (vertical) */
  x0: number;
  /** right edge x (vertical) */
  x1: number;
  yTL: number;
  yTR: number;
  yBR: number;
  yBL: number;
}

export type H = [number, number, number, number, number, number, number, number];

const STAGE = 1024;

/** Solves the 8-DOF homography taking the stage square onto the measured slot quad. */
export function solveHomography(q: Quad): H {
  const src: Array<[number, number]> = [
    [0, 0],
    [STAGE, 0],
    [STAGE, STAGE],
    [0, STAGE],
  ];
  const dst: Array<[number, number]> = [
    [q.x0, q.yTL],
    [q.x1, q.yTR],
    [q.x1, q.yBR],
    [q.x0, q.yBL],
  ];
  const A: number[][] = [];
  const B: number[] = [];
  for (let i = 0; i < 4; i++) {
    const [sx, sy] = src[i];
    const [dx, dy] = dst[i];
    A.push([sx, sy, 1, 0, 0, 0, -dx * sx, -dx * sy]);
    B.push(dx);
    A.push([0, 0, 0, sx, sy, 1, -dy * sx, -dy * sy]);
    B.push(dy);
  }
  const n = 8;
  const M = A.map((row, i) => [...row, B[i]]);
  for (let i = 0; i < n; i++) {
    let p = i;
    for (let r = i; r < n; r++) if (Math.abs(M[r][i]) > Math.abs(M[p][i])) p = r;
    [M[i], M[p]] = [M[p], M[i]];
    for (let r = 0; r < n; r++) {
      if (r !== i && Math.abs(M[i][i]) > 1e-12) {
        const f = M[r][i] / M[i][i];
        for (let c = 0; c <= n; c++) M[r][c] -= f * M[i][c];
      }
    }
  }
  return M.map((row, i) => row[n] / row[i]) as H;
}

/** Projects a stage-space point through the homography into image space. */
export function applyH(h: H, x: number, y: number): [number, number] {
  const [a, b, c, d, e, f, g, hh] = h;
  const w = g * x + hh * y + 1;
  return [(a * x + b * y + c) / w, (d * x + e * y + f) / w];
}

/** The homography as a CSS matrix3d, for transforming the whole book layer at once. */
export function matrix3d(h: H): string {
  const [a, b, c, d, e, f, g, hh] = h;
  const m = [a, d, 0, g, b, e, 0, hh, 0, 0, 1, 0, c, f, 0, 1];
  return `matrix3d(${m.map((v) => v.toFixed(6)).join(', ')})`;
}

/**
 * Container-space fractions for screen-space divider x positions.
 *
 * The dividers are measured on the photograph; each book needs its left/right edge in the flat
 * pre-projection space, which is what the bisection recovers.
 */
export function slotBounds(h: H, dividers: number[]): number[] {
  const uFor = (target: number): number => {
    let lo = 0;
    let hi = STAGE;
    for (let i = 0; i < 50; i++) {
      const mid = (lo + hi) / 2;
      const [x] = applyH(h, mid, STAGE / 2);
      if (x < target) lo = mid;
      else hi = mid;
    }
    return (lo + hi) / 2 / STAGE;
  };
  return [0, ...dividers.map(uFor), 1];
}
